import { Component, OnInit, inject, signal, computed, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, Validators, ReactiveFormsModule, AbstractControl, FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { environment } from '../../../environments/environment';

// Services
import { BusinessService } from '../../../shared/services/business.service';
import { StaffService } from '../../../shared/services/staff.service';
import { ModuleService } from '../../../shared/services/module.service';
import { NavigationService } from '../../../core/services/navigation.service';
import { LoadingService } from '../../../core/services/loading.service';

// Models
import { Business, ApiResponse, Staff } from '../../../shared/models';

// UI Components
import { AppButton } from '../../../shared/components/app-button/app-button';
import { AppInput } from '../../../shared/components/app-input/app-input';
import { AppSelect, SelectOption } from '../../../shared/components/app-select/app-select';
import { DialogComponent as AppDialog } from '../../../shared/components/app-dialog/app-dialog';
import { firstValueFrom } from 'rxjs';
import { AppSectionHeader } from "../../../shared/components/app-section-header/app-section-header";

@Component({
  selector: 'app-business-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, AppButton, AppInput, AppSelect, AppDialog, AppSectionHeader],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './business-form.html',
  styleUrl: './business-form.css'
})
export class BusinessForm implements OnInit {
  // ─── Dependencies ──────────────────────────────────────────────────────────
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private businessService = inject(BusinessService);
  private staffService = inject(StaffService);
  private moduleService = inject(ModuleService);
  private nav = inject(NavigationService);
  private loadingService = inject(LoadingService);

  // ─── Route State ────────────────────────────────────────────────────────────
  private params = toSignal(this.route.params);

  /** Returns the business ID from route params if present */
  get businessParam(): string | null {
    return this.params()?.['id'] || null;
  }

  currentBusiness = signal<Business | null>(null);
  saving = signal(false);
  error = signal('');

  // File Preview State
  logoPreview = signal<string | null>(null);
  signatureFileName = signal<string | null>(null);

  // Collaborative State
  collaborators = signal<Staff[]>([]);
  loadingCollaborators = signal(false);

  // Transit Files
  private logoFile: File | null = null;
  private signatureFile: File | null = null;

  // Static Data
  readonly envOptions: SelectOption[] = [
    { label: 'Pruebas (TEST)', value: 'TEST' },
    { label: 'Producción (PRODUCTION)', value: 'PRODUCTION' }
  ];

  // ─── Form Definition ───────────────────────────────────────────────────────
  form = this.fb.group({
    // Identity
    name: ['', [Validators.required]],
    commercialName: [''],
    taxId: ['', [Validators.required]],

    // Contact
    address: ['', [Validators.required]],
    establishmentAddress: [''],
    phone: ['', [Validators.required]],
    email: ['', [Validators.required, Validators.email]],

    // Invoicing Configuration (conditional based on hasSignature)
    hasSignature: [false],
    establishmentCode: ['001'],
    emissionPointCode: ['001'],
    sequentialInvoice: [1],
    environment: ['TEST'],
    invoiceApiKey: [''],
    signaturePassword: [''],
    invoiceEstablishmentId: [''],
    invoiceEmissionPointId: [''],

    // Taxation
    obligadoContabilidad: [false],
    isSpecialTaxpayer: [false],
    specialTaxpayerResolution: [''],
    regimenType: ['REGIMEN GENERAL'],

    // Status & Permissions
    isEnabled: [true],
    staffLimit: [0],
    allowedActions: [[] as string[]],
  });

  // ─── Modules & Permissions Management ────────────────────────────────────
  allModules = signal<any[]>([]);

  /** Available modules mapped for UI display */
  availableModules = computed(() => {
    return this.allModules()
      .filter(m => m.group !== 'system')
      .map(m => ({
        name: m.key,
        label: m.label,
        icon: m.icon,
        actions: (m.permissions || []).map((p: string) => {
          const key = p.split(':')[1] || p;
          const labels: Record<string, string> = {
            list: 'Listar', create: 'Registrar', edit: 'Editar',
            delete: 'Eliminar', export: 'Exportar', print: 'Imprimir',
            sync: 'Sincronizar', manage: 'Gestionar',
            odontogram: 'Odontograma', sri: 'Facturación SRI'
          };
          return {
            key,
            label: labels[key] || key.charAt(0).toUpperCase() + key.slice(1)
          };
        })
      }));
  });

  loadModules() {
    this.moduleService.loadAll({ all: true }).subscribe({
      next: (resp: any) => this.allModules.set(resp.data || [])
    });
  }

  private get _currentPermissions(): string[] {
    return this.form.get('allowedActions')?.value ?? [];
  }

  isPermissionEnabled(perm: string): boolean {
    return this._currentPermissions.includes(perm);
  }

  isModuleFullyEnabled(module: any): boolean {
    return module.actions.every((a: { key: string }) => this.isPermissionEnabled(`${module.name}:${a.key}`));
  }

  isModulePartial(module: any): boolean {
    const enabled = module.actions.filter((a: { key: string }) => this.isPermissionEnabled(`${module.name}:${a.key}`)).length;
    return enabled > 0 && enabled < module.actions.length;
  }

  togglePermission(perm: string, event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    const current = [...this._currentPermissions];

    if (checked) {
      if (!current.includes(perm)) current.push(perm);
    } else {
      const idx = current.indexOf(perm);
      if (idx > -1) current.splice(idx, 1);
    }
    this.form.get('allowedActions')?.setValue(current);
  }

  toggleModule(module: any, event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    const current = [...this._currentPermissions];

    module.actions.forEach((a: { key: string }) => {
      const perm = `${module.name}:${a.key}`;
      if (checked && !current.includes(perm)) current.push(perm);
      if (!checked) {
        const i = current.indexOf(perm);
        if (i > -1) current.splice(i, 1);
      }
    });
    this.form.get('allowedActions')?.setValue(current);
  }

  getAllowedCount(): number {
    return this._currentPermissions.length;
  }

  grantAllPermissions() {
    const all = this.availableModules().flatMap(m => m.actions.map((a: { key: string }) => `${m.name}:${a.key}`));
    this.form.get('allowedActions')?.setValue([...new Set(all)]);
  }

  clearAllPermissions() {
    this.form.get('allowedActions')?.setValue([]);
  }

  goBack() {
    this.nav.goBack();
  }

  // ─── Lifecycle Hook ────────────────────────────────────────────────────────
  ngOnInit() {
    this.loadModules();

    const id = this.businessParam;
    if (id) {
      this.loadBusiness(id);
    }

    // Reactive validation for invoicing fields
    this.form.get('hasSignature')!.valueChanges.subscribe((val) => {
      this.toggleInvoicingValidators(!!val);
    });
  }

  getLogoUrl(path?: string): string | null {
    if (!path) return null;
    if (path.startsWith('http') || path.startsWith('data:')) return path;

    // Si es un path relativo de uploads, concatenar la base del API del backend
    const baseUrl = environment.apiUrl.replace('/api/v1', '').replace(/\/$/, '');
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    return `${baseUrl}/${cleanPath}`;
  }

  // ─── Data Persistence Logic ───────────────────────────────────────────────
  loadBusiness(id: string) {
    this.loadingService.showLoading('Cargando Negocio', 'Preparando información administrativa...');
    this.businessService.getById(id).subscribe({
      next: (resp: any) => {
        const business = resp.data || resp;
        this.currentBusiness.set(business);

        // Reset previews when loading a new business
        this.logoPreview.set(null);
        this.signatureFileName.set(null);
        this.logoFile = null;
        this.signatureFile = null;

        this.form.patchValue({
          ...business,
          allowedActions: business.allowedActions ?? [],
          isEnabled: business.isEnabled !== false,
        });

        this.loadingService.hideLoading();
        this.loadCollaborators(id);
      },
      error: () => this.loadingService.hideLoading()
    });
  }

  save() {
    this.form.markAllAsTouched();
    if (this.form.invalid) return;

    this.saving.set(true);
    this.error.set('');

    const id = this.businessParam;
    const data: any = this.form.getRawValue();

    // Clean up invoicing fields if not enabled
    if (!data.hasSignature) {
      const fieldsToClear = [
        'establishmentCode', 'emissionPointCode', 'sequentialInvoice',
        'environment', 'invoiceApiKey', 'signaturePassword',
        'invoiceEstablishmentId', 'invoiceEmissionPointId'
      ];
      fieldsToClear.forEach(f => delete data[f]);
    } else {
      // Garantizar que conservamos los IDs si vienen del currentBusiness y no han sido cambiados
      if (!data.invoiceEstablishmentId && this.currentBusiness()?.invoiceEstablishmentId) {
        data.invoiceEstablishmentId = this.currentBusiness()?.invoiceEstablishmentId;
      }
      if (!data.invoiceEmissionPointId && this.currentBusiness()?.invoiceEmissionPointId) {
        data.invoiceEmissionPointId = this.currentBusiness()?.invoiceEmissionPointId;
      }
    }

    // Incluimos archivos y password en el payload (se enviarán como FormData)
    data.logo = this.logoFile || undefined;
    data.signature = this.signatureFile || undefined;

    const request$ = id
      ? this.businessService.update(id, data as Partial<Business>)
      : this.businessService.create(data as Partial<Business>);

    request$.subscribe({
      next: (res: any) => {
        this.saving.set(false);
        this.logoFile = null;
        this.signatureFile = null;

        if (!id && data.invoiceApiKey) {
          const newId = res.data?.id || res.id || res._id;
          this.router.navigate(['/app/business/edit', newId]);
        } else {
          this.router.navigate(['/app/business']);
        }
      },
      error: (err) => {
        this.saving.set(false);
        this.error.set(err?.error?.message || 'Error al procesar la solicitud');
      }
    });
  }

  syncWithInvoiceApi() {
    const apiKey = this.form.get('invoiceApiKey')?.value;
    if (!apiKey) {
      this.error.set('Debe ingresar un API Key para sincronizar');
      return;
    }

    this.saving.set(true);
    const id = this.businessParam;

    // Sincronizar (PULL de datos ÚNICAMENTE)
    this.businessService.syncInvoicing(id || '', apiKey || undefined).subscribe({
      next: (res: any) => {
        const updated = res.data || res;
        this.currentBusiness.set(updated);

        // Patch form with pulled data
        this.form.patchValue({
          ...updated,
          allowedActions: updated.allowedActions ?? this.form.get('allowedActions')?.value,
        });

        this.error.set('');
        this.saving.set(false);
      },
      error: (err) => {
        this.saving.set(false);
        this.error.set(err?.error?.message || 'Error al sincronizar con el Invoice API');
      }
    });
  }

  // ─── Collaborators Management ────────────────────────────────────────────
  loadCollaborators(business: string) {
    this.loadingCollaborators.set(true);
    this.staffService.getByBusiness(business).subscribe({
      next: (res: ApiResponse<Staff[]>) => {
        const data = (res.data || []).map(m => ({
          ...m,
          id: m.id || (m as any)._id
        }));
        this.collaborators.set(data);
        this.loadingCollaborators.set(false);
      },
      error: () => this.loadingCollaborators.set(false)
    });
  }

  removeCollaborator(staff: string) {
    if (!confirm('¿Estás seguro de quitar el acceso a este usuario en este negocio?')) return;

    this.staffService.delete(staff).subscribe({
      next: () => {
        this.collaborators.update(list => list.filter(m => m.id !== staff));
      },
      error: (err) => {
        this.error.set(err?.error?.message || 'Error al quitar colaborador');
      }
    });
  }

  addCollaborator() {
    this.router.navigate(['/app/business/edit', this.businessParam, 'staff', 'new']);
  }

  // ─── Form & UI Helpers ───────────────────────────────────────────────────
  private toggleInvoicingValidators(required: boolean) {
    const invoicingFields: string[] = ['establishmentCode', 'emissionPointCode'];
    invoicingFields.forEach((field) => {
      const ctrl: AbstractControl | null = this.form.get(field);
      if (!ctrl) return;

      if (required) {
        ctrl.setValidators([Validators.required]);
      } else {
        ctrl.clearValidators();
      }
      ctrl.updateValueAndValidity({ emitEvent: false });
    });
  }

  fieldError(name: string): string {
    const ctrl = this.form.get(name);
    if (!ctrl?.touched && !ctrl?.dirty) return '';
    if (ctrl?.hasError('required')) return 'Este campo es requerido';
    if (ctrl?.hasError('email')) return 'Email inválido';
    return '';
  }

  onFileSelected(event: any, _field: string) {
    const file = event.target.files[0];
    if (!file) return;

    // Validar tamaño (2 MB máx)
    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      this.error.set('La imagen es demasiado pesada (máximo 2MB)');
      event.target.value = ''; // Limpiar el input
      return;
    }

    this.logoFile = file;
    const reader = new FileReader();
    reader.onload = () => {
      this.logoPreview.set(reader.result as string);
    };
    reader.readAsDataURL(file);
  }

  onSignatureFileSelected(event: any) {
    const file: File = event.target.files[0];
    if (!file) return;

    this.signatureFile = file;
    this.signatureFileName.set(file.name);
  }

  clearLogoSelection() {
    this.logoFile = null;
    this.logoPreview.set(null);
  }

  clearSignatureSelection() {
    this.signatureFile = null;
    this.signatureFileName.set(null);
    this.form.get('signaturePassword')?.reset();
  }

  // ─── Interactive Sync Logic ──────────────────────────────────────────────
  isSyncModalOpen = signal(false);
  syncLoading = signal(false);
  remoteEstablishments = signal<any[]>([]);
  remoteEmissionPoints = signal<any[]>([]);
  selectedEstablishmentId = signal<string>('');
  selectedEmissionPointId = signal<string>('');
  tempEstCode = signal('');
  tempPntCode = signal('');

  // Creation State
  isCreatingEst = signal(false);
  isCreatingPnt = signal(false);
  newEst = signal({ code: '', name: '', address: '', phone: '', email: '' });
  newPnt = signal({ code: '', sequential: 1 });
  syncError = signal('');

  get establishmentOptions(): any[] {
    const options = this.remoteEstablishments().map(e => ({
      label: `${e.code} - ${e.name || e.description || 'Sin nombre'}`,
      value: e._id || e.id
    }));
    return options;
  }

  get emissionPointOptions(): any[] {
    const options = this.remoteEmissionPoints().map(ep => ({
      label: `${ep.code} - ${ep.sequential || 0} ${ep.isMain ? '(Principal)' : ''}`,
      value: ep._id || ep.id
    }));
    return options;
  }

  async openSyncModal() {
    const apiKey = this.form.get('invoiceApiKey')?.value;
    if (!apiKey) {
      this.error.set('Debe ingresar un API Key para sincronizar');
      return;
    }

    const profile: any = await firstValueFrom(this.businessService.getRemoteBusiness(apiKey));
    this.form.patchValue({
      name: profile.name,
      taxId: profile.taxId,
      email: profile.email,
      phone: profile.phone,
      address: profile.address,
      environment: profile.environment

    });
    this.isSyncModalOpen.set(true);
    this.syncLoading.set(true);
    this.syncError.set('');
    this.resetCreationState();
    const id = this.businessParam || 'new';

    this.businessService.getRemoteEstablishments(id, apiKey || undefined).subscribe({
      next: (data) => {
        this.remoteEstablishments.set(data);
        this.syncLoading.set(false);

        // Pre-seleccionar si ya existe en el local
        const currentEst = this.form.get('establishmentCode')?.value;
        if (currentEst) {
          const match = data.find((e: any) => String(e.code) === String(currentEst));
          if (match) {
            this.onEstablishmentChange(match._id || match.id);
          }
        }
      },
      error: (err) => {
        this.syncLoading.set(false);
        this.error.set(err?.error?.message || 'Error al obtener establecimientos remotos');
      }
    });
  }

  onEstablishmentChange(estId: string) {
    const bizId = this.businessParam || 'new';
    const apiKey = this.form.get('invoiceApiKey')?.value;
    if (!estId || !apiKey) return;

    this.selectedEstablishmentId.set(estId);
    this.remoteEmissionPoints.set([]);
    this.selectedEmissionPointId.set('');
    this.isCreatingPnt.set(false);

    const est = this.remoteEstablishments().find(e => (e._id || e.id) === estId);
    if (est) this.tempEstCode.set(est.code);

    this.businessService.getRemoteEmissionPoints(bizId, estId, undefined, apiKey || undefined).subscribe({
      next: (data) => {
        this.remoteEmissionPoints.set(data);

        // Pre-seleccionar punto de emisión
        const currentPnt = this.form.get('emissionPointCode')?.value;
        if (currentPnt) {
          const match = data.find((p: any) => String(p.code || p.emissionPoint) === String(currentPnt));
          if (match) {
            this.onEmissionPointChange(match._id || match.id);
          }
        }
      }
    });
  }

  onEmissionPointChange(epId: string) {
    this.selectedEmissionPointId.set(epId);
    const ep = this.remoteEmissionPoints().find(p => (p._id || p.id) === epId);
    if (ep) this.tempPntCode.set(ep.code || ep.emissionPoint);
  }

  // --- Remote Creation Methods ---

  toggleCreateEst() {
    this.isCreatingEst.update(v => !v);
    this.syncError.set('');
    if (this.isCreatingEst()) {
      this.newEst.set({
        code: '',
        name: this.form.get('commercialName')?.value || '',
        address: this.form.get('address')?.value || '',
        phone: this.form.get('phone')?.value || '',
        email: this.form.get('email')?.value || ''
      });
    }
  }

  toggleCreatePnt() {
    this.isCreatingPnt.update(v => !v);
    this.syncError.set('');
    if (this.isCreatingPnt()) {
      this.newPnt.set({ code: '', sequential: 1 });
    }
  }

  createRemoteEstablishment() {
    const apiKey = this.form.get('invoiceApiKey')?.value;
    const bizId = this.businessParam || 'new';
    const payload = { ...this.newEst(), isActive: true, isMain: this.remoteEstablishments().length === 0 };

    this.syncLoading.set(true);
    this.syncError.set('');

    this.businessService.createRemoteEstablishment(bizId, payload, apiKey || undefined).subscribe({
      next: (resp) => {
        const created = resp.data || resp;
        this.remoteEstablishments.update(list => [...list, created]);
        this.isCreatingEst.set(false);
        this.syncLoading.set(false);
        this.onEstablishmentChange(created._id || created.id);
      },
      error: (err) => {
        this.syncLoading.set(false);
        // ApiService ya formatea el mensaje en err.message
        this.syncError.set(err.message || 'Error al crear establecimiento');
      }
    });
  }

  createRemoteEmissionPoint() {
    const apiKey = this.form.get('invoiceApiKey')?.value;
    const bizId = this.businessParam || 'new';
    const estId = this.selectedEstablishmentId();
    if (!estId) return;

    const payload = {
      ...this.newPnt(),
      establishmentId: estId,
      isActive: true,
      isMain: this.remoteEmissionPoints().length === 0
    };

    this.syncLoading.set(true);
    this.syncError.set('');

    this.businessService.createRemoteEmissionPoint(bizId, payload, apiKey || undefined).subscribe({
      next: (resp) => {
        const created = resp.data || resp;
        this.remoteEmissionPoints.update(list => [...list, created]);
        this.isCreatingPnt.set(false);
        this.syncLoading.set(false);
        this.onEmissionPointChange(created._id || created.id);
      },
      error: (err) => {
        this.syncLoading.set(false);
        this.syncError.set(err.message || 'Error al crear punto de emisión');
      }
    });
  }

  applySyncSelection() {
    const estId = this.selectedEstablishmentId();
    const epId = this.selectedEmissionPointId();

    if (!estId || !epId) return;

    const est = this.remoteEstablishments().find(e => (e._id || e.id) === estId);
    const ep = this.remoteEmissionPoints().find(p => (p._id || p.id) === epId);

    if (est && ep) {
      this.form.patchValue({
        establishmentCode: est.code,
        emissionPointCode: ep.code || ep.emissionPoint,
        establishmentAddress: ep.address || est.address || this.form.get('establishmentAddress')?.value,
        invoiceEstablishmentId: est._id || est.id,
        invoiceEmissionPointId: ep._id || ep.id
      });
      this.closeSyncModal();
    }
  }

  closeSyncModal() {
    this.isSyncModalOpen.set(false);
    this.remoteEstablishments.set([]);
    this.remoteEmissionPoints.set([]);
    this.selectedEstablishmentId.set('');
    this.selectedEmissionPointId.set('');
    this.resetCreationState();
  }

  resetCreationState() {
    this.isCreatingEst.set(false);
    this.isCreatingPnt.set(false);
    this.syncError.set('');
    this.newEst.set({ code: '', name: '', address: '', phone: '', email: '' });
    this.newPnt.set({ code: '', sequential: 1 });
  }
}
